import { APP_NAME } from 'app-config'

import { IRequestOptions, IResponseResult } from 'models/api.model'
import { TAnswers } from 'models/common.model'
import {
  IAccountStatusResponseRaw,
  IBindUserResponseRaw,
  IGetUserStatusResponseRaw,
  ILearnWorldsResponseRaw,
  IPredictedLtvResponseRaw,
  IUpiPurchaseData,
} from 'models/user.model'

import { ApiService } from 'services/api.service'

import { RequestMethod, TEST_ENV_QUERY_PARAM } from 'root-constants'
import { TAppName } from 'root-types'

export class UserApi {
  private api: ApiService
  private readonly baseUrlV1 = 'v1/flow'
  private readonly baseUrlV2 = 'v2/leads'
  private readonly appName: TAppName = APP_NAME

  constructor(api: ApiService) {
    this.api = api
  }

  getUserStatus(
    uuid: string,
  ): Promise<IResponseResult<IGetUserStatusResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.GET,
    }

    return this.api.makeRequest<string>(
      `${this.baseUrlV2}/${uuid}/${this.appName}`,
      options,
    )
  }

  getAccountStatus(
    email: string,
  ): Promise<IResponseResult<IAccountStatusResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        app_name: this.appName,
        email: email.toLowerCase(),
      },
    }

    return this.api.makeRequest<IAccountStatusResponseRaw>(
      `${this.baseUrlV2}/account/status`,
      options,
    )
  }

  bindUser({
    token,
    uuid,
  }: {
    token: string
    uuid: string
  }): Promise<IResponseResult<IBindUserResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        token,
      },
    }

    return this.api.makeRequest<IBindUserResponseRaw>(
      `${this.baseUrlV2}/${uuid}/${this.appName}/signup`,
      options,
    )
  }

  getAutologinToken(
    uuid: string,
  ): Promise<IResponseResult<{ status: boolean; custom_token: string }>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        uuid,
        app_name: this.appName,
      },
    }

    return this.api.makeRequest<never>(`${this.baseUrlV1}/login`, options)
  }

  saveFacebookParams({
    fbc,
    fbp,
    uuid,
  }: {
    uuid: string
    fbc: string
    fbp: string
  }): Promise<IResponseResult<string>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        fbc,
        fbp,
      },
    }

    return this.api.makeRequest<IResponseResult<string>>(
      `${this.baseUrlV2}/${uuid}/${this.appName}/facebook`,
      options,
    )
  }

  saveUserAnswers({
    uuid,
    answers,
    isFinished = false,
    isSkipped = false,
  }: {
    uuid: string
    answers: TAnswers
    isFinished?: boolean
    isSkipped?: boolean
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        is_finished: isFinished,
        is_skipped: isSkipped,
        quiz: answers,
        version: '2.0',
      },
    }

    return this.api.makeRequest<never>(
      `${this.baseUrlV2}/${uuid}/${this.appName}/quiz`,
      options,
    )
  }

  saveUserEmail({
    uuid,
    email,
  }: {
    uuid: string
    email: string
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        email: email.toLowerCase(),
      },
    }

    return this.api.makeRequest<never>(
      `${this.baseUrlV2}/${uuid}/${this.appName}/email`,
      options,
    )
  }

  saveUserEmailConsent({
    uuid,
    consented,
  }: {
    uuid: string
    consented: boolean
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        consented,
      },
    }

    return this.api.makeRequest<never>(
      `${this.baseUrlV2}/${uuid}/${APP_NAME}/email/consent`,
      options,
    )
  }

  saveUserEmailAndPassword({
    uuid,
    email,
    password,
  }: {
    uuid: string
    email: string
    password: string
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        login: email.toLocaleLowerCase(),
        password,
      },
    }

    return this.api.makeRequest<never>(
      `${this.baseUrlV2}/${uuid}/${this.appName}/learnworlds/signup`,
      options,
    )
  }

  getLearnWorldsUserToken(
    uuid: string,
  ): Promise<IResponseResult<ILearnWorldsResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
    }

    return this.api.makeRequest<never>(
      `${this.baseUrlV2}/${uuid}/${this.appName}/learnworlds/token`,
      options,
    )
  }

  saveUserConfig({
    uuid,
    config,
  }: {
    uuid: string
    config: Record<
      string,
      | string
      | boolean
      | number
      | string[]
      | Record<string, string>
      | Record<string, number>
      | IUpiPurchaseData
      | null
    >
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        config,
      },
    }

    return this.api.makeRequest<never>(
      `${this.baseUrlV2}/${uuid}/${this.appName}/config`,
      options,
    )
  }

  getUUID({
    cohort,
    appId,
    locale,
    promoCode = '',
    utm,
  }: {
    cohort: string
    appId: string
    locale: string
    promoCode?: string
    utm: Record<string, string>
  }): Promise<IResponseResult<string>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        cohort,
        locale,
        app_id: appId,
        app_name: this.appName,
        utm,
        promo_code: promoCode,
        is_test_environment:
          document.location.search.includes(TEST_ENV_QUERY_PARAM),
      },
    }

    return this.api.makeRequest<IGetUserStatusResponseRaw>(
      this.baseUrlV2,
      options,
    )
  }

  saveUserTTCLID({
    ttclid,
    uuid,
  }: {
    ttclid: string
    uuid: string
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      params: { ttclid },
    }

    return this.api.makeRequest(`${this.baseUrlV2}/${uuid}/tiktok`, options)
  }

  saveUserRDTCID({
    rdtcid,
    uuid,
  }: {
    rdtcid: string
    uuid: string
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      params: { rdt_cid: rdtcid },
    }

    return this.api.makeRequest(`${this.baseUrlV2}/${uuid}/reddit`, options)
  }

  saveUserSCCID({
    sccid,
    scid,
    uuid,
  }: {
    sccid: string
    scid: string
    uuid: string
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      params: { sccid, uuid_c1: scid },
    }

    return this.api.makeRequest(`${this.baseUrlV2}/${uuid}/snapchat`, options)
  }

  saveUserEPIK({
    epik,
    uuid,
  }: {
    epik: string
    uuid: string
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      params: { epik },
    }

    return this.api.makeRequest(`${this.baseUrlV2}/${uuid}/pinterest`, options)
  }

  getPredictedLtv(
    uuid: string,
  ): Promise<IResponseResult<IPredictedLtvResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.GET,
    }

    return this.api.makeRequest<IPredictedLtvResponseRaw>(
      `${this.baseUrlV2}/${uuid}/${this.appName}/predicted_ltv`,
      options,
    )
  }
}
